import { CountBird, Icon, type IconName } from '@league/ui';
import { useQuery } from '@tanstack/react-query';
import { clsx } from 'clsx';
import type { CSSProperties, ReactNode } from 'react';
import { NavLink } from 'react-router-dom';

import { apiFetch } from '../lib/api';

/**
 * The left rail, shared by the director and commissioner shells.
 *
 * 236px on `--deep`. Text on it uses the `on-deep` trio, which is NOT interchangeable with the
 * ordinary ink tokens: `text-ink-2` on navy is 1.6:1, and that is the specific mistake
 * UI-UX-SPEC §2 calls out in the mockups.
 *
 * Below 1024px the rail becomes a horizontal scroller across the top. Both shells are laptop-first
 * — a director does seating at a table with a laptop open, and the commissioner works at a desk —
 * but neither should break on a tablet, and a rail that becomes a hamburger hides the whole
 * information architecture behind a tap.
 */
export interface RailItem {
  to: string;
  label: string;
  icon: IconName;
  /** Matches nested routes too: `/director/leagues` stays lit on `/director/leagues/abc`. */
  end?: boolean;
}

export interface RailGroup {
  /** Omitted on the director rail, which is a flat list. */
  title?: string;
  items: RailItem[];
}

/**
 * THE SAME LOCKUP AS EVERYWHERE ELSE.
 *
 * The bird and the name in the display face — identical to the public site's header and to the
 * sign-in card, because signing in should not feel like arriving at a different product. It was
 * missing here, so the one place somebody spends hours was the one place with no brand on it.
 *
 * The name comes from the same config value every other surface reads. Nothing here writes it down,
 * and the mark itself is `aria-hidden` because the wordmark beside it already says it.
 *
 * `navy`, because the rail IS the deep panel. The rendition follows the ground, never the page.
 */
function Wordmark({ brand }: { brand: string }) {
  /*
    THE MASTHEAD IS THE WAY BACK OUT, which every other site on the internet has taught people to
    expect — and it was a plain `<div>`, so clicking the mark did nothing at all.

    The public site is a DIFFERENT ORIGIN, so this is an anchor rather than a route: React Router
    would look for `/` inside the signed-in app and find the dashboard again. Where that origin is
    depends on the deployment, so it is read rather than written down.

    Read from the cache `App` already filled — same query key, `staleTime: Infinity` — so this costs
    no second request. `enabled: false` keeps it from fetching on its own if it ever renders first;
    with no answer yet it falls back to a plain block, which is what it always was.
  */
  const meta = useQuery<{ siteOrigin?: string }>({
    queryKey: ['meta'],
    queryFn: () => apiFetch('/api/meta'),
    staleTime: Infinity,
    enabled: false,
  });

  const inside = (
    <>
      {/*
        72, not 48 — owner's call, 2026-08-30: the mark shows its full 1-5-2 tail wherever it is a
        masthead. 48 fell in the 40–71 band, where the component serves the six-feather medium
        drawing and concedes the exact count.
      */}
      <CountBird size={72} ground="navy" className="shrink-0" />
      <span className="font-display text-[17px] leading-none whitespace-nowrap text-on-deep">
        {brand}
      </span>
    </>
  );

  const site = meta.data?.siteOrigin;

  if (!site) {
    return <div className="flex shrink-0 items-center gap-2.5 px-1">{inside}</div>;
  }

  return (
    <a
      href={site}
      /* No focus utilities here: the base stylesheet sets the ring, and `focusOnDeep` is
         deliberately not emitted as a utility — an unknown Tailwind class fails silently. */
      className="flex shrink-0 items-center gap-2.5 px-1"
      /* It leaves the app, and a screen reader should say so before the click rather than after. */
      aria-label={`${brand} home page`}
    >
      {inside}
    </a>
  );
}

export function Rail({
  brand,
  groups,
  pinned,
  header,
  footer,
  narrowHeader,
}: {
  brand: string;
  groups: RailGroup[];
  /**
   * Items held at the bottom, out of the scrolling list — Settings, which would otherwise be the
   * fourteenth entry on a rail that scrolls on a 900px laptop. On a narrow screen the rail is one
   * horizontal strip, so these simply join the end of it.
   */
  pinned?: RailGroup[];
  /** The "Viewing as" switcher on the commissioner's rail; nothing on the director's. */
  header?: ReactNode;
  footer?: ReactNode;
  /** Beside the wordmark below 1024px, where the footer is not shown. */
  narrowHeader?: ReactNode;
}) {
  return (
    <nav
      aria-label="Main"
      /**
       * The focus ring is redefined for everything inside the rail. The page's ring is `--deep`,
       * which against this navy is 1.00:1 — the ring IS the ground, so a keyboard user tabbing the
       * navigation would see nothing at all. `--on-deep` is 12.55:1 here. Measured, not eyeballed.
       */
      style={{ '--focus-ring': 'var(--brand-focus-on-deep)' } as CSSProperties}
      className={clsx(
        'flex shrink-0 flex-col bg-deep text-on-deep',
        'lg:h-screen lg:w-[236px] lg:sticky lg:top-0',
      )}
    >
      {/*
        Stacked on a laptop, side by side on a narrow screen. Below 1024px the rail becomes a
        horizontal scroller, and the wordmark deliberately does NOT join that strip: it would
        scroll away, and a brand mark that disappears when you reach for the fourth tab is worse
        than none. It keeps a fixed bar above, which is what the public site's header does anyway.
      */}
      <div className="flex items-center gap-3 border-b border-on-deep/12 p-3 lg:flex-col lg:items-stretch">
        <Wordmark brand={brand} />
        {header ? <div className="min-w-0 flex-1 lg:flex-none">{header}</div> : null}
        {narrowHeader ? <div className="min-w-0 flex-1 lg:hidden">{narrowHeader}</div> : null}
      </div>

      <div
        className={clsx(
          'rail-scroll flex gap-1 overflow-x-auto p-3',
          'lg:flex-1 lg:flex-col lg:overflow-y-auto lg:overflow-x-visible',
        )}
      >
        {groups.map((group, index) => (
          <Group key={group.title ?? index} group={group} />
        ))}

        {/* On a narrow rail there is no bottom to pin to, so these just join the end of the strip. */}
        {pinned?.map((group, index) => (
          <Group key={`pinned-${index}`} group={group} className="lg:hidden" />
        ))}
      </div>

      {pinned || footer ? (
        <div className="hidden flex-col gap-3 p-3 lg:flex">
          {pinned?.map((group, index) => <Group key={`pinned-lg-${index}`} group={group} />)}
          {footer}
        </div>
      ) : null}
    </nav>
  );
}

function Group({ group, className }: { group: RailGroup; className?: string }) {
  return (
    <div className={clsx('flex gap-1 lg:flex-col lg:gap-0.5', className)}>
      {group.title ? (
/*
  `on-deep-2`, never `-3`, for anything this small.

  Under the previous palette the third step was 2.97:1 here and this rule was a hard-won correction.
  Navy & Chalk fixes the token itself — `--on-deep-3` is 4.85:1 — so the rule is no longer load
  bearing, but it stays: WCAG's 3:1 allowance is about SIZE, needing 24px or 18.66px bold, and every
  one of these renders at 11px. Nothing here should ever go near the bottom of a scale again.

  The hierarchy survives without the colour: these are small, uppercase and tracked, and the items
  under them are larger and sentence-case.
*/
        <p className="hidden px-3 pt-3.5 pb-1 text-[11px] font-semibold uppercase tracking-[0.09em] text-on-deep-2 lg:block">
          {group.title}
        </p>
      ) : null}

      {group.items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end ?? false}
          className={({ isActive }) =>
            clsx(
              'flex items-center gap-2.5 rounded px-3 py-2 text-[13.5px] whitespace-nowrap transition-colors',
              isActive
                ? /*
                   * A tinted panel, not just brighter text. On a dark ground a weight change alone
                   * is close to invisible, and the current section is the one thing the rail has to
                   * answer at a glance.
                   */
                  'bg-on-deep/14 font-semibold text-on-deep'
                : 'text-on-deep-2 hover:bg-on-deep/8 hover:text-on-deep',
            )
          }
        >
          <Icon name={item.icon} size={17} />
          {item.label}
        </NavLink>
      ))}
    </div>
  );
}

/** Who is signed in, and the way out. Pinned to the bottom of the rail on a laptop. */
export function UserChip({
  name,
  role,
  onSignOut,
  accountHref,
}: {
  name: string;
  role: string;
  onSignOut: () => void;
  /** Where her own account lives, when the menu has no Settings (an instructor, 2026-10-16). */
  accountHref?: string;
}) {
  return (
    <div className="flex items-center justify-between gap-2 rounded border border-on-deep/15 px-3 py-2.5">
      <div className="min-w-0">
        {accountHref ? (
          <NavLink to={accountHref} className="block truncate text-[13px] font-semibold text-on-deep underline underline-offset-4 decoration-on-deep-3 hover:decoration-on-deep">{name}</NavLink>
        ) : (
          <p className="truncate text-[13px] font-semibold text-on-deep">{name}</p>
        )}
        <p className="text-[11px] uppercase tracking-[0.1em] text-on-deep-2">{role}</p>
      </div>
      <button
        type="button"
        onClick={onSignOut}
        title="Sign out"
        aria-label="Sign out"
        className="rounded p-1.5 text-on-deep-2 transition-colors hover:bg-on-deep/12 hover:text-on-deep"
      >
        <Icon name="signOut" size={17} title="Sign out"
        aria-label="Sign out" />
      </button>
    </div>
  );
}
